import {
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Clock,
  Shield,
  Search,
  Fish,
  TrendingUp,
  Activity,
  ThumbsUp,
  Sparkles,
} from 'lucide-react'
import { useState } from 'react'
import type { AIAnalysisResponse, Severity, FindingStatus, DetailedAnalysis, FishImpact } from '../types'
import { RecommendationsList } from './RecommendationsList'
import { Timeline } from './Timeline'

interface RootCauseResultProps {
  result: AIAnalysisResponse
}

// Severity configuration for issue_found scenarios
const SEVERITY_CONFIG: Record<Exclude<Severity, 'none'>, {
  bg: string
  border: string
  text: string
  icon: React.ElementType
  label: string
  description: string
}> = {
  low: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: CheckCircle,
    label: 'Low Concern',
    description: 'Minor issue that can be addressed at your convenience.'
  },
  medium: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    icon: AlertCircle,
    label: 'Moderate Concern',
    description: 'Action recommended within the next few days.'
  },
  high: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
    icon: AlertTriangle,
    label: 'High Concern',
    description: 'Prompt attention required to prevent escalation.'
  },
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: XCircle,
    label: 'Critical Issue',
    description: 'Immediate action required to prevent significant losses.'
  },
}

// Positive messages for all-clear results
const ALL_CLEAR_MESSAGES = [
  "Your pond management practices are paying off!",
  "The data shows healthy, stable conditions.",
  "Keep up the great work with your pond care!",
  "Your attention to detail is keeping this pond thriving.",
]

// Get a random positive message (computed once at module load time)
const RANDOM_POSITIVE_MESSAGE = ALL_CLEAR_MESSAGES[Math.floor(Math.random() * ALL_CLEAR_MESSAGES.length)]

export function RootCauseResult({ result }: RootCauseResultProps) {
  const [showDetails, setShowDetails] = useState(false)

  // Parse detailedAnalysis - handle both object (REST) and JSON string (ConnectRPC/proto)
  let detailedAnalysis: DetailedAnalysis = {}
  if (result.detailedAnalysis) {
    detailedAnalysis = result.detailedAnalysis
  } else if (result.detailedAnalysisJson) {
    try {
      detailedAnalysis = JSON.parse(result.detailedAnalysisJson) as DetailedAnalysis
    } catch (e) {
      console.error('Failed to parse detailedAnalysisJson:', e)
    }
  }

  // Determine finding status - default to issue_found for backwards compatibility
  const findingStatusFromAnalysis = detailedAnalysis?.finding_status as FindingStatus | undefined
  const findingStatus: FindingStatus = result.findingStatus ||
    findingStatusFromAnalysis ||
    (result.severity === 'none' ? 'all_clear' : 'issue_found')

  const isAllClear = findingStatus === 'all_clear'
  const severity = result.severity || 'medium'
  const whatHappened = detailedAnalysis.whatHappened || detailedAnalysis.what_happened
  const whyItHappened = detailedAnalysis.whyItHappened || detailedAnalysis.why_it_happened
  const primaryCause = detailedAnalysis.primaryCause || (detailedAnalysis.root_cause_chain?.[0])
  const contributingFactors = detailedAnalysis.contributingFactors || []
  const rootCauseChain = detailedAnalysis.rootCauseChain || detailedAnalysis.root_cause_chain || []
  const preventionMeasures = detailedAnalysis.preventionMeasures || []
  const dataPointsAnalyzed = detailedAnalysis.dataPointsAnalyzed || detailedAnalysis.data_points_analyzed
  const keyObservations = detailedAnalysis.key_observations || detailedAnalysis.keyObservations || []
  const positiveIndicators = detailedAnalysis.positive_indicators || detailedAnalysis.positiveIndicators || []

  // Get fish impact with snake_case fallback (can come from result or detailedAnalysis)
  const fishImpactFromAnalysis = (detailedAnalysis.fish_impact || detailedAnalysis.fishImpact) as FishImpact | undefined
  const fishImpact = result.fishImpact || fishImpactFromAnalysis
  const ifUnaddressed = fishImpact?.ifUnaddressed || fishImpact?.if_unaddressed
  const timelineToImpact = fishImpact?.timelineToImpact || fishImpact?.timeline_to_impact
  const affectedFishPercentage = fishImpact?.affectedFishPercentage || fishImpact?.affected_fish_percentage
  const reversible = fishImpact?.reversible

  const confidencePercent = result.confidenceScore ? Math.round(result.confidenceScore * 100) : null

  // Use module-level constant for positive message (avoids impure function during render)
  const positiveMessage = RANDOM_POSITIVE_MESSAGE

  // All Clear Result
  if (isAllClear) {
    return (
      <div className="space-y-5">
        {/* All Clear Header */}
        <div className="rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="rounded-full p-3 bg-emerald-100">
                  <ThumbsUp className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-xl font-semibold text-emerald-900">
                    All Clear
                  </h3>
                  <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                    <Sparkles className="h-3.5 w-3.5" />
                    Healthy
                  </span>
                </div>
                <p className="mt-2 text-sm text-emerald-700 font-medium">
                  {result.summary || "No significant issues detected in your pond data."}
                </p>
              </div>
            </div>

            {/* Positive Feedback */}
            <div className="mt-5 pt-4 border-t border-emerald-200/50">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/60">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                <p className="text-sm text-emerald-800 font-medium">
                  {positiveMessage}
                </p>
              </div>
            </div>

            {/* Confidence Score */}
            {confidencePercent !== null && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-emerald-700 font-medium">Analysis Confidence</span>
                  <span className="font-semibold text-emerald-600">{confidencePercent}%</span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-white/80">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-700 ease-out"
                    style={{ width: `${confidencePercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Summary */}
        <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-white to-slate-50/50 p-6 shadow-sm space-y-5">
          <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">Quick Summary</h4>

          {/* What Was Checked */}
          {whatHappened && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-slate-700">
                <Search className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium text-sm">What We Found</span>
              </div>
              <p className="text-sm text-slate-600 pl-6">{whatHappened}</p>
            </div>
          )}

          {/* Data Points */}
          {dataPointsAnalyzed && (
            <div className="flex items-center gap-3 text-sm pl-6">
              <Activity className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-slate-600">
                Analyzed <span className="font-semibold text-slate-800">{dataPointsAnalyzed}</span> data points
              </span>
            </div>
          )}

          {/* Key Observations for All Clear */}
          {keyObservations.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-700">
                <Lightbulb className="h-4 w-4 flex-shrink-0 text-amber-500" />
                <span className="font-medium text-sm">Key Observations</span>
              </div>
              <ul className="pl-6 space-y-2">
                {keyObservations.map((observation: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-slate-400 mt-0.5">•</span>
                    {observation}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Positive Indicators for All Clear */}
          {positiveIndicators.length > 0 && (
            <div className="space-y-3 pt-3 border-t border-slate-200/50">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium text-sm">Healthy Indicators</span>
              </div>
              <ul className="pl-6 space-y-2">
                {positiveIndicators.map((indicator: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {indicator}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Keep It Up Tips */}
        {preventionMeasures.length > 0 && (
          <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Keep Up the Good Work
            </h4>
            <p className="text-sm text-slate-500 mb-3">
              Continue these practices to maintain healthy pond conditions:
            </p>
            <ul className="space-y-2">
              {preventionMeasures.map((measure: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {measure}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Timeline if present */}
        {result.timelineEvents && result.timelineEvents.length > 0 && (
          <div>
            <h4 className="mb-3 font-medium text-slate-900">Recent Events Analyzed</h4>
            <Timeline events={result.timelineEvents} farmId={result.farmId} pondId={result.pondId} />
          </div>
        )}
      </div>
    )
  }

  // Issue Found Result
  const severityConfig = SEVERITY_CONFIG[severity === 'none' ? 'low' : severity]
  const SeverityIcon = severityConfig.icon

  return (
    <div className="space-y-5">
      {/* Issue Header */}
      <div className={`rounded-xl border-2 ${severityConfig.border} ${severityConfig.bg} overflow-hidden`}>
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className={`rounded-full p-3 ${severity === 'critical' ? 'bg-red-100' : severity === 'high' ? 'bg-orange-100' : severity === 'medium' ? 'bg-yellow-100' : 'bg-green-100'}`}>
                <Search className={`h-6 w-6 ${severityConfig.text}`} />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-xl font-semibold text-slate-900">
                  Issue Identified
                </h3>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${severityConfig.bg} ${severityConfig.text} border ${severityConfig.border}`}>
                  <SeverityIcon className="h-3.5 w-3.5" />
                  {severityConfig.label}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-700 font-medium">
                {result.summary || primaryCause || "An issue has been detected that requires attention."}
              </p>
            </div>
          </div>

          {/* Confidence Score */}
          {confidencePercent !== null && (
            <div className="mt-5 pt-4 border-t border-slate-200/50">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Analysis Confidence</span>
                <span className={`font-semibold ${confidencePercent >= 80 ? 'text-emerald-600' : confidencePercent >= 60 ? 'text-yellow-600' : 'text-orange-600'}`}>
                  {confidencePercent}%
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-white/80">
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${confidencePercent >= 80 ? 'bg-emerald-500' : confidencePercent >= 60 ? 'bg-yellow-500' : 'bg-orange-500'}`}
                  style={{ width: `${confidencePercent}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Summary - What Happened & Why */}
      <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-white to-slate-50/50 p-6 shadow-sm space-y-5">
        <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">Quick Summary</h4>

        {/* What Happened */}
        {whatHappened && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-slate-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium text-sm">What Happened</span>
            </div>
            <p className="text-sm text-slate-600 pl-6">{whatHappened}</p>
          </div>
        )}

        {/* Why It Happened */}
        {whyItHappened && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-amber-700">
              <Lightbulb className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium text-sm">Why It Happened</span>
            </div>
            <p className="text-sm text-slate-600 pl-6">{whyItHappened}</p>
          </div>
        )}

        {/* Key Observations */}
        {keyObservations.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-slate-700">
              <Search className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium text-sm">Key Observations</span>
            </div>
            <ul className="pl-6 space-y-2">
              {keyObservations.map((observation: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-slate-400 mt-0.5">•</span>
                  {observation}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Root Cause Chain */}
        {rootCauseChain.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-purple-700">
              <Activity className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium text-sm">Root Cause Chain</span>
            </div>
            <div className="pl-6 space-y-2">
              {rootCauseChain.map((cause: string, idx: number) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center mt-0.5">
                    {idx + 1}
                  </span>
                  <p className="text-sm text-slate-600">{cause}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fish Impact Warning */}
      {ifUnaddressed && (
        <div className={`rounded-xl border-2 ${severity === 'critical' || severity === 'high' ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'} p-5`}>
          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
            <Fish className={`h-5 w-5 ${severity === 'critical' || severity === 'high' ? 'text-red-500' : 'text-orange-500'}`} />
            Impact on Fish if Not Addressed
          </h4>

          <div className="space-y-3">
            <p className={`text-sm ${severity === 'critical' || severity === 'high' ? 'text-red-800' : 'text-orange-800'} font-medium`}>
              {ifUnaddressed}
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              {timelineToImpact && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <div>
                    <span className="text-slate-500">Timeline:</span>
                    <span className="ml-1 font-medium text-slate-700">{timelineToImpact}</span>
                  </div>
                </div>
              )}

              {affectedFishPercentage && (
                <div className="flex items-center gap-2 text-sm">
                  <Fish className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <div>
                    <span className="text-slate-500">At Risk:</span>
                    <span className="ml-1 font-medium text-slate-700">{affectedFishPercentage}</span>
                  </div>
                </div>
              )}

              {reversible !== undefined && (
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <div>
                    <span className="text-slate-500">Reversible:</span>
                    <span className={`ml-1 font-medium ${reversible ? 'text-emerald-600' : 'text-red-600'}`}>
                      {reversible ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contributing Factors */}
      {contributingFactors.length > 0 && (
        <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Contributing Factors
          </h4>
          <ul className="space-y-2">
            {contributingFactors.map((factor: string, idx: number) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                <span className="text-amber-500 mt-0.5">•</span>
                {factor}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Detailed Analysis - Collapsible */}
      {(primaryCause || preventionMeasures.length > 0 || dataPointsAnalyzed) && (
        <div className="rounded-xl border border-slate-200/50 bg-white shadow-sm overflow-hidden">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex w-full items-center justify-between p-5 text-left hover:bg-slate-50/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              <span className="font-medium text-slate-900">Detailed Analysis & Prevention</span>
            </div>
            {showDetails ? (
              <ChevronUp className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            )}
          </button>
          {showDetails && (
            <div className="border-t border-slate-100 p-5 space-y-5">
              {/* Primary Cause */}
              {primaryCause && (
                <div>
                  <h5 className="text-sm font-medium text-slate-900 mb-2">Primary Cause</h5>
                  <p className="text-sm text-slate-600 leading-relaxed">{primaryCause}</p>
                </div>
              )}

              {/* Data Points Analyzed */}
              {dataPointsAnalyzed && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Activity className="h-4 w-4" />
                  <span>Based on analysis of {dataPointsAnalyzed} data points</span>
                </div>
              )}

              {/* Prevention Measures */}
              {preventionMeasures.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-slate-900 mb-2">Prevention for the Future</h5>
                  <ul className="space-y-2">
                    {preventionMeasures.map((measure: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {measure}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {result.recommendations && result.recommendations.length > 0 && (
        <div>
          <h4 className="mb-3 font-medium text-slate-900 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Recommended Actions
          </h4>
          <RecommendationsList recommendations={result.recommendations} />
        </div>
      )}

      {/* Timeline Events */}
      {result.timelineEvents && result.timelineEvents.length > 0 && (
        <div>
          <h4 className="mb-3 font-medium text-slate-900">Related Events</h4>
          <Timeline events={result.timelineEvents} farmId={result.farmId} pondId={result.pondId} />
        </div>
      )}
    </div>
  )
}

export default RootCauseResult
